<template>
  <section class="hero-section" :style="{ backgroundImage: `url(${heroBg})` }">
    <div class="overlay">
      <!-- Top-left: Create Announcement button (only for managers/supermanagers) -->
      <div class="header-actions" v-if="user && (user.role === 'manager' || user.role === 'supermanager')">
        <v-btn color="primary" class="create-announcement-btn" @click="goToAnnouncementCreate" variant="elevated">
          Create Announcement
        </v-btn>
      </div>


      <div v-if="user && user.role !== 'onboarding'">
        <v-row class="announcements-row" dense>
          <v-col v-for="announcement in filteredAnnouncements" :key="announcement.id" cols="12" sm="6" md="4">
            <v-card class="announcement-card" outlined>
              <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ announcement.title }}</span>
                <div class="icon-buttons">
                  <v-btn v-if="user.role === 'manager' || user.role === 'supermanager'" icon color="primary"
                    @click="goToAnnouncementEdit(announcement.id)" class="icon-btn">
                    <SvgIcon :path="mdiPencil" size="18" style="color:white;vertical-align:middle;" />
                  </v-btn>
                  <v-btn v-if="user.role === 'manager' || user.role === 'supermanager'" icon color="error"
                    @click="confirmDelete(announcement.id)" class="icon-btn">
                    <SvgIcon :path="mdiDelete" size="18" style="color:white;vertical-align:middle;" />
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <div @click="onAnnouncementClick">
                  <ArticleContent
                    :key="['ann', announcement.id, announcement.current_history_id || 'no-current', announcement.last_read_history_id || 'no-lastread', openTick].join(':')"
                    :latestJson="announcement.__latest_norm"
                    :lastReadJson="announcement.__lastread_norm"
                    :latestHistoryId="announcement.current_history_id || announcement.latest_history_id || announcement.id"
                    :lastReadHistoryId="announcement.last_read_history_id || null"
                    :diffHtml="announcement.diff_html || ''"
                    :latestHtml="announcement.__latest_html"
                    :openTick="openTick"
                  />
                  <div class="expires-text" v-if="announcement.expires_at">
                    Expires: <span>{{ formatDate(announcement.expires_at) }}</span>
                  </div>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />

                <!-- centre area: button or green check -->
                <template v-if="announcement.ack_status !== 'read'">
                  <v-btn tonal small color="primary" @click="ackAnnouncement(announcement)">
                    Mark as read
                  </v-btn>
                </template>
                <template v-else>
                  <v-tooltip>
                    <template #activator="{ props }">
                      <SvgIcon v-bind="props" :path="iconForStatus('read').path" size="24"
                        :class="['status-main', iconForStatus('read').css]" />
                    </template>
                    <span>{{ iconForStatus('read').tooltip }}</span>
                  </v-tooltip>
                </template>

                <v-spacer />
              </v-card-actions>

              <!-- bottom‑right corner icon for unread / updated -->
              <div v-if="announcement.ack_status !== 'read'" class="corner-status">
                <v-tooltip>
                  <template #activator="{ props }">
                    <SvgIcon v-bind="props" :path="iconForStatus(announcement.ack_status).path" size="20"
                      :class="iconForStatus(announcement.ack_status).css" />
                  </template>
                  <span>{{ iconForStatus(announcement.ack_status).tooltip }}</span>
                </v-tooltip>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
    <HandbookArticleModal :showDialog="chain.length > 0" :article="currentArticle" :chainLength="chain.length"
      :prevTitle="prevTitle" :isManager="false" origin="home" @mention-link-clicked="mentionLinkClicked"
      @pop-one="popOne" @close-all="closeAll" @prefetch-chain-link="prefetchSectionById" />
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import api from '@/plugins/axios';
import { getSectionMaybeCached, putSection } from '@/utils/handbookCache';
import { useUserStore } from '@/stores/user';
import heroBg from '@/assets/hero-bg.jpeg';
import { mdiPencil, mdiDelete } from '@mdi/js';
import { iconForStatus } from '@/utils/ackStatus';
import { normalizeForSave } from '@/tiptap/diff/normalizeForSave'

import HandbookArticleModal from '@/views/HandbookArticleModal.vue';
import ArticleContent from '@/tiptap/components/ArticleContent.vue'

// dev-only logger to avoid ESLint "no-empty" while keeping prod silent
function logDev(label, err) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug(label, err);
  }
}




const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const user = computed(() => userStore.user);

const announcements = ref([]);
const loadingAnnouncements = ref(true);
const openTick = ref(0)

const chain = ref([]);
const currentArticle = computed(() => chain.value[chain.value.length - 1] || {});
const prevTitle      = computed(() =>
  chain.value.length > 1 ? chain.value[chain.value.length - 2].title : ''
);

// only publish announcements not expired and matching user.bar
// only publish announcements not expired and matching user.bar (base list)
const filteredAnnouncementsBase = computed(() => {
  if (!user.value) return [];
  if (user.value.role === 'onboarding') return [];
  const now = new Date();
  const currentBar =
    user.value.role === 'supermanager' && user.value.active_bar
      ? user.value.active_bar
      : user.value.bar;
  return announcements.value.filter(a =>
    (!a.expires_at || new Date(a.expires_at) > now) && a.bar === currentBar
  );
});

function normDoc(doc) {
  try {
    if (doc && doc.type === 'doc') return normalizeForSave(doc);
  } catch (_) { /* noop */ }
  return { type: 'doc', content: [] };
}

// normalized, render-ready list mirroring Handbook modal invariants
const filteredAnnouncements = computed(() =>
  filteredAnnouncementsBase.value.map(a => ({
    ...a,
    __latest_norm: normDoc(a.latest_json || a.content_json || null),
    __lastread_norm: a.last_read_json
      ? normDoc(a.last_read_json)
      : null,
    __latest_html: a.content_html_latest || a.content_html || a.content || '',
  }))
);

function formatDate(dateStr) {
  return dateStr ? new Date(dateStr).toLocaleString() : '';
}

function goToAnnouncementCreate() {
  router.push('/announcement/create');
}

function goToAnnouncementEdit(id) {
  router.push(`/announcement/edit/${id}`);
}


function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

async function deleteAnnouncement(id) {
  const csrftoken = getCookie('csrftoken') || '';
  try {
    const response = await fetch(`/api/announcements/${id}/`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'X-CSRFToken': csrftoken }
    });
    if (!response.ok) throw new Error('Failed to delete announcement.');
    await fetchAnnouncements();
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
}

function confirmDelete(id) {
  if (confirm('Are you sure you want to delete this announcement?')) {
    deleteAnnouncement(id);
  }
}

async function onAnnouncementClick(e) {
  const a = e.target.closest('a');
  if (!a) return;
  const href = a.getAttribute('href') || '';
  const m = href.match(/\/handbook\/article\/(\d+)/);
  if (!m) return;

  e.preventDefault();
  e.stopPropagation();
  const id = Number(m[1]);
  chain.value.push({ id, title: 'Article' });
}

async function mentionLinkClicked({ newId }) {
  const id = Number(newId);
  chain.value.push({ id, title: 'Article' });
}

function popOne() {
  chain.value.pop();
}

function closeAll() {
  chain.value = [];
}

async function prefetchSectionById(id) {
  const cid = Number(id);
  if (getSectionMaybeCached(cid)) return;
  try {
    const { data } = await api.get(`/api/handbook-sections/${cid}/`, { withCredentials: true });
    putSection(data);
  } catch (err) {
    logDev('home: prefetchSectionById failed', err);
  }
}


async function fetchAnnouncements() {
  loadingAnnouncements.value = true;
  try {
    const response = await api.get('/api/announcements/', { withCredentials: true });
    announcements.value = response.data;
    await nextTick();
    openTick.value++;
  } catch (err) {
    console.error('Error fetching announcements:', err);
    announcements.value = [];
  } finally {
    loadingAnnouncements.value = false;
  }
}

async function ackAnnouncement(announcement) {
  if (!window.confirm("By clicking Yes, you acknowledge that you have read and understood this announcement.")) {
    return;
  }
  try {
    await api.post(
      `/api/announcements/${announcement.id}/acknowledge/`,
      {},
      { withCredentials: true }
    );
    let updated = null;
    try {
      // Pull canonical fresh payload so diff/read state clears immediately.
      const { data } = await api.get(
        `/api/announcements/${announcement.id}/?_cb=${Date.now()}`,
        {
          withCredentials: true,
          headers: { 'Cache-Control': 'no-cache' },
        }
      );
      updated = data;
    } catch (refreshErr) {
      // Fallback to optimistic update if detail refresh fails.
      const curId = announcement.current_history_id || announcement.latest_history_id || null;
      const latestJson = announcement.latest_json || announcement.content_json || { type: 'doc', content: [] };
      updated = {
        ...announcement,
        ack_status: 'read',
        last_read_history_id: curId,
        last_read_id: curId,
        last_read_json: latestJson,
        __lastread_norm: normDoc(latestJson),
      };
      if (process.env.NODE_ENV !== 'production') {
        console.debug('announcement ack follow-up refresh failed', refreshErr);
      }
    }

    // Replace array element to trigger Vue reactivity immediately.
    const idx = announcements.value.findIndex(a => a.id === announcement.id);
    if (idx !== -1 && updated) announcements.value.splice(idx, 1, updated);
    openTick.value++;
  } catch (err) {
    console.error('Error acknowledging announcement:', err);
  }
}

onMounted(async () => {
  // try to restore session if needed
  if (!user.value) {
    await userStore.fetchUser();
  }
  // only load announcements if logged in
  if (user.value) {
    await fetchAnnouncements();
  }
});

// re-fetch when active_bar changes
watch(
  () => user.value && user.value.active_bar,
  async (newBar, oldBar) => {
    if (newBar !== oldBar) await fetchAnnouncements();
  },
  { immediate: false }
);

// handle URL ?switch_bar=...
watch(
  () => route.query.switch_bar,
  async newBar => {
    if (newBar) {
      try {
        await api.post(
          '/api/switch-bar/',
          { bar: newBar },
          { withCredentials: true }
        );
        await userStore.fetchUser();
        await fetchAnnouncements();
      } catch (err) {
        console.error('Error switching bar:', err);
      }
      const q = { ...route.query };
      delete q.switch_bar;
      router.replace({ query: q });
    }
  }
);

</script>

<style scoped>
.hero-section {
  position: relative;
  width: 100%;
  height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 20px;
}
.overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.1);
  padding: 20px;
  overflow-y: auto;
}
.header-actions {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
}
.announcements-row {
  margin-top: 48px;
}
.announcement-card {
  background-color: rgba(255, 255, 255, 0.9);
  position: relative; /* enable absolute children */
}
.expires-text {
  font-size: 0.85rem;
  margin-top: 10px;
  color: #555;
}
.icon-buttons {
  display: flex;
  gap: 4px;
}
.icon-btn {
  padding: 4px;
}


.corner-status {
  position: absolute;
  bottom: 1px;  /* slightly closer to bottom edge */
  right: 8px;   /* unchanged */
}
.status-main {
  margin-right: 4px;
}

/* TipTap viewer basics inside announcement cards */
.announcement-card .content img { max-width: 100%; height: auto; }
.announcement-card .content iframe { display: block; max-width: 100%; border: 0; }
.announcement-card .content ul,
.announcement-card .content ol { margin-left: 1.5em; padding-left: 1.5em; list-style-position: outside; }
.announcement-card .content li { margin-bottom: 0.5em; }

/* Keep TipTap viewer/diffviewer transparent inside announcement cards only */
.announcement-card :deep(.vuetify-pro-tiptap-editor__content),
.announcement-card :deep(.vuetify-pro-tiptap-editor__content .content),
.announcement-card :deep(.vuetify-pro-tiptap-editor__content .markdown-theme-github) {
  background: transparent !important;
  box-shadow: none !important;
  border: 0 !important;
}

/* Make sure common block elements don’t reintroduce a background */
.announcement-card :deep(.content p),
.announcement-card :deep(.content pre),
.announcement-card :deep(.content code),
.announcement-card :deep(.content blockquote) {
  background: transparent !important;
}

/* Announcement card content: improved word breaking for long words/URLs etc */
.announcement-card :deep(.content p),
.announcement-card :deep(.content li),
.announcement-card :deep(.content span),
.announcement-card :deep(.content a) {
  overflow-wrap: anywhere;   /* allow wrapping anywhere if needed */
  word-break: normal;        /* prefer natural breaks when possible */
  hyphens: auto;             /* show hyphens when splitting long words */
  line-height: 1.35;
}

/* Optional: keep tables readable without a white box (tweak/remove if you want) */
.announcement-card :deep(table) {
  background: transparent !important;
}
.announcement-card :deep(th),
.announcement-card :deep(td) {
  background: rgba(255,255,255,0.6); /* delete this line for full transparency */
}
</style>  
